import { useSyncExternalStore } from "react";
import { AuthRequestError, refreshAuthSession, type AuthSession } from "@/api/authApi";
import { rememberAccountState } from "@/auth/accountState";

export type LandingIdentity =
  | { kind: "loading" }
  | { kind: "guest" }
  | { kind: "authenticated"; session: AuthSession };

let identity: LandingIdentity = { kind: "loading" };
let refreshPromise: Promise<AuthSession> | null = null;
let bootstrapPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();
const retryDelays = [250, 750] as const;

function publish(value: LandingIdentity) {
  identity = value;
  // Remembering the resolved state lets the next load paint the right account entry immediately;
  // only the state itself is stored, never the access token or profile data.
  if (value.kind === "authenticated") rememberAccountState("authenticated");
  else if (value.kind === "guest") rememberAccountState("guest");
  listeners.forEach((listener) => listener());
}

export function getLandingIdentity() {
  return identity;
}

export function useLandingIdentity() {
  return useSyncExternalStore(
    (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
    getLandingIdentity,
    getLandingIdentity,
  );
}

export function establishAuthSession(session: AuthSession) {
  publish({ kind: "authenticated", session });
}

export function clearAuthSession() {
  publish({ kind: "guest" });
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function refreshWithRetry(): Promise<AuthSession> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await refreshAuthSession();
    } catch (error) {
      const unauthenticated = error instanceof AuthRequestError && error.kind === "unauthenticated";
      const delay = retryDelays[attempt];
      if (unauthenticated || delay === undefined) throw error;
      await wait(delay);
    }
  }
}

export function renewAuthSession(): Promise<AuthSession> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = refreshWithRetry()
    .then((session) => { establishAuthSession(session); return session; })
    .catch((error: unknown) => {
      if (error instanceof AuthRequestError && error.kind === "unauthenticated") clearAuthSession();
      throw error;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

export function bootstrapAuthSession(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = renewAuthSession()
    .then(() => undefined)
    .catch(() => undefined)
    .finally(() => { bootstrapPromise = null; });
  return bootstrapPromise;
}
